import React, { useState } from 'react';
import { Button, Select, Form } from 'antd';
import { Colors } from '@common/tokens';
import {
  ButtonGroup,
  Drawer,
  Gap,
  Gutter,
  Text,
  TextArea,
  Title,
} from '@common/components';
import { OfferRejectionReasons } from '@common/graphql';

const { Option, OptGroup } = Select;

export const OfferCommonRejectionDrawer = ({
  title,
  onDrawerClose,
  open,
  onRejectClick,
  buttonLoading,
}: {
  title: string;
  open: boolean;
  onDrawerClose: () => void;
  onRejectClick: (
    rejectionReason: OfferRejectionReasons | null,
    rejectionDetails: string
  ) => void;
  buttonLoading: boolean;
}) => {
  const [rejectionReason, setRejectionReason] =
    useState<OfferRejectionReasons | null>(null);
  const [rejectionDetails, setRejectionDetails] = useState('');

  const isFormValid =
    rejectionReason &&
    (!rejectionReasonRequiresDetails(rejectionReason) || rejectionDetails);

  const showRejectionReasonInput =
    rejectionReason && rejectionReasonRequiresDetails(rejectionReason);

  const onClose = () => {
    setRejectionReason(null);
    setRejectionDetails('');
    onDrawerClose();
  };

  const onReject = () => {
    onRejectClick(rejectionReason, rejectionDetails);
    onClose();
  };

  const textAreaPlaceholder = buildTextAreaPlaceholder(rejectionReason);

  return (
    <>
      <Drawer
        className="RejectReviewDrawer"
        open={open}
        title={title}
        onClose={onDrawerClose}
        footer={
          <ButtonGroup>
            <Button size="large" onClick={onClose}>
              Cancel
            </Button>
            <Button
              danger
              disabled={!isFormValid}
              type="primary"
              loading={buttonLoading}
              onClick={onReject}
              size="large"
            >
              Reject
            </Button>
          </ButtonGroup>
        }
      >
        <section className="reject-warning-section">
          <Gutter size="md" asPadding>
            <Gutter size="md" direction="bottom">
              <Gap vertical>
                <Title>Reject Offer</Title>
                <Text>Please select the reason for the offer rejection.</Text>
              </Gap>
            </Gutter>

            <Form
              onValuesChange={({ rejectionReason, rejectionDetails }) => {
                if (rejectionReason) setRejectionReason(rejectionReason);
                if (rejectionDetails) setRejectionDetails(rejectionDetails);
              }}
            >
              <Form.Item
                name="rejectionReason"
                rules={[{ required: true, message: 'Please choose an option' }]}
              >
                <Select
                  placeholder="Select a reason"
                  className="rejection-reason-select"
                  style={{ width: '100%' }}
                >
                  <Option value="no_longer_available">
                    No longer available
                  </Option>
                  <Option value="interested_if_adjusted">
                    Interested, if offer is adjusted
                  </Option>
                  <Option value="other_unresponsive">Unresponsive</Option>
                  <Option value="other_other">Other</Option>
                  <OptGroup label="Accepted competing offer:">
                    <Option value="competing_bill_rate">Bill Rate</Option>
                    <Option value="competing_dates">Dates</Option>
                    <Option value="competing_location">Location</Option>
                    <Option value="competing_shift">Shift</Option>
                    <Option value="competing_other">Other</Option>
                  </OptGroup>
                </Select>
              </Form.Item>

              {showRejectionReasonInput && (
                <Form.Item
                  name="rejectionDetails"
                  rules={[
                    {
                      required: true,
                      message: 'Please specify a reason',
                    },
                  ]}
                >
                  <TextArea
                    rows={4}
                    maxLength={500}
                    placeholder={textAreaPlaceholder}
                  />
                </Form.Item>
              )}
            </Form>
          </Gutter>
        </section>
      </Drawer>
      <style jsx>{`
        :global(.RejectReviewDrawer .reject-warning-section) {
          border-radius: 4px;
          background-color: ${Colors.coralHyperlight};
        }
      `}</style>
    </>
  );
};

function buildTextAreaPlaceholder(
  rejectionReason: OfferRejectionReasons | null
) {
  if (rejectionReason === 'interested_if_adjusted') {
    return 'Please specify';
  }
  return 'If other, specify a reason';
}

function rejectionReasonRequiresDetails(rejectionReason: string) {
  return ['other_other', 'competing_other', 'interested_if_adjusted'].includes(
    rejectionReason
  );
}
